import React, { useEffect, useState } from "react";
import type { V2Schedule } from "../../../models";
import { fetchV2Schedules } from "../../../api/clientV2";
import { useWBSTree } from "./useWBSTree";
import TreeNode from "../TreeNode/TreeNode";
import "./TreeView.css";

const TreeView: React.FC = () => {
  const [schedules, setSchedules] = useState<V2Schedule[]>([]);
  const [selectedScheduleId, setSelectedScheduleId] = useState<number | null>(
    null
  );
  const [schedulesLoading, setSchedulesLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchV2Schedules()
      .then((data) => {
        setSchedules(data);
        if (data.length > 0) {
          setSelectedScheduleId(data[0].id);
        }
        setSchedulesLoading(false);
      })
      .catch(() => {
        setError("Could not connect to API v2 (port 5051). Is it running?");
        setSchedulesLoading(false);
      });
  }, []);

  if (schedulesLoading) {
    return (
      <div className="tree-view">
        <div className="tree-loading">Loading schedules...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tree-view">
        <div className="tree-error">{error}</div>
      </div>
    );
  }

  if (!selectedScheduleId) {
    return (
      <div className="tree-view">
        <div className="tree-empty">
          <p>No schedules found. Ingest an XER file first.</p>
        </div>
      </div>
    );
  }

  return (
    <TreeViewContent
      scheduleId={selectedScheduleId}
      scheduleName={
        schedules.find((s) => s.id === selectedScheduleId)?.name || ""
      }
    />
  );
};

interface ContentProps {
  scheduleId: number;
  scheduleName: string;
}

const TreeViewContent: React.FC<ContentProps> = ({
  scheduleId,
  scheduleName,
}) => {
  const {
    roots,
    rootsLoading,
    expandAllLoading,
    loadRoots,
    toggleNode,
    expandAll,
    collapseAll,
    getChildren,
    getActivities,
    isExpanded,
    isLoading,
  } = useWBSTree(scheduleId);

  useEffect(() => {
    loadRoots();
  }, [loadRoots]);

  const totalActivities = roots.reduce((s, r) => s + r.activity_count, 0);
  const totalComplete = roots.reduce(
    (s, r) => s + r.status_summary.complete,
    0
  );
  const totalActive = roots.reduce(
    (s, r) => s + r.status_summary.active,
    0
  );
  const totalNotStarted = roots.reduce(
    (s, r) => s + r.status_summary.not_started,
    0
  );

  return (
    <div className="tree-view">
      <div className="tree-toolbar">
        <div className="tree-toolbar-left">
          <span className="tree-schedule-name">{scheduleName}</span>
          <div className="tree-actions">
            <button
              className="tree-btn"
              onClick={expandAll}
              disabled={expandAllLoading}
            >
              {expandAllLoading ? "Loading..." : "Expand All"}
            </button>
            <button className="tree-btn" onClick={collapseAll}>
              Collapse All
            </button>
          </div>
        </div>
        <div className="tree-summary">
          <span>{totalActivities} activities</span>
          <span className="tree-summary-complete">{totalComplete} complete</span>
          <span className="tree-summary-active">{totalActive} active</span>
          <span className="tree-summary-notstart">
            {totalNotStarted} not started
          </span>
        </div>
      </div>

      <div className="tree-container">
        {rootsLoading ? (
          <div className="tree-loading">Loading WBS tree...</div>
        ) : (
          roots.map((node) => (
            <TreeNode
              key={node.id}
              node={node}
              depth={0}
              isExpanded={isExpanded(node.id)}
              isLoading={isLoading(node.id)}
              children={getChildren(node.id)}
              activities={getActivities(node.id)}
              onToggle={toggleNode}
              getChildren={getChildren}
              getActivities={getActivities}
              isNodeExpanded={isExpanded}
              isNodeLoading={isLoading}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default TreeView;
